/* eslint-disable @typescript-eslint/no-explicit-any */
// services/monitoring/MonitoringService.ts
import { SentryAdapter } from './Sentry';
import { MonitoringAdapter } from './types';

class MonitoringService {
  private adapter: MonitoringAdapter;

  private initialized: boolean = false;

  constructor(adapter: MonitoringAdapter) {
    this.adapter = adapter;
  }

  initialize(initOptions: unknown): void {
    if (this.adapter.initialize) {
      this.adapter.initialize(initOptions);
    }
    this.initialized = true;
  }

  setAdapter(adapter: MonitoringAdapter): void {
    this.adapter = adapter;
    this.initialized = false;
  }

  // ===== USER =====
  setUser(
    userId: string | null,
    options?: { id: string; email?: string; username?: string }
  ): void {
    if (!this.initialized) {
      console.warn('Monitoring service not initialized');
      return;
    }
    this.adapter.setUser(userId, options);
  }

  clearUser(): void {
    if (!this.initialized) {
      console.warn('Monitoring service not initialized');
      return;
    }
    this.adapter.clearUser();
  }

  // ===== TAGS =====
  setTag(key: string, value: string | number | boolean): void {
    if (!this.initialized) {
      console.warn('Monitoring service not initialized');
      return;
    }
    this.adapter.setTag(key, value);
  }

  setTags(tags: Record<string, string | number | boolean>): void {
    if (!this.initialized) {
      console.warn('Monitoring service not initialized');
      return;
    }
    this.adapter.setTags(tags);
  }

  clearTag(key: string): void {
    if (!this.initialized) {
      console.warn('Monitoring service not initialized');
      return;
    }
    this.adapter.clearTag(key);
  }

  // ===== CONTEXT =====
  setContext(key: string, context: Record<string, unknown>): void {
    if (!this.initialized) {
      console.warn('Monitoring service not initialized');
      return;
    }
    this.adapter.setContext(key, context);
  }

  clearContext(key: string): void {
    if (!this.initialized) {
      console.warn('Monitoring service not initialized');
      return;
    }
    this.adapter.clearContext(key);
  }

  // ===== BREADCRUMBS =====
  addBreadcrumb(
    message: string,
    options?: {
      category?: string;
      level?: 'info' | 'debug' | 'warning' | 'error';
      data?: Record<string, unknown>;
    }
  ): void {
    if (!this.initialized) {
      console.warn('Monitoring service not initialized');
      return;
    }
    this.adapter.addBreadcrumb(message, options);
  }

  // ===== ERRORS =====
  captureException(
    error: unknown,
    context?: {
      tags?: Record<string, unknown>;
      extra?: Record<string, unknown>;
    }
  ): void {
    if (!this.initialized) {
      console.warn('Monitoring service not initialized');
      return;
    }
    this.adapter.captureException(error, context);
  }

  captureMessage(
    message: string,
    level: 'info' | 'debug' | 'warning' | 'error' = 'info',
    context?: {
      tags?: Record<string, unknown>;
      extra?: Record<string, unknown>;
    }
  ): void {
    if (!this.initialized) {
      console.warn('Monitoring service not initialized');
      return;
    }
    this.adapter.captureMessage(message, level, context);
  }

  // ===== SCOPE =====
  withScope(callback: (scope: any) => void): void {
    if (!this.initialized) {
      console.warn('Monitoring service not initialized');
      return;
    }
    if (this.adapter.withScope) {
      this.adapter.withScope(callback);
    }
  }
}

const monitoringService = new MonitoringService(new SentryAdapter());
export default monitoringService;
